import { useEffect, useRef } from "react";
import {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
} from "../store/admin/notificationSlice";
import { useUserType } from "./useUserType";

/**
 * Custom hook for real-time notification polling
 * @param {Object} options - Polling configuration
 * @param {number} options.interval - Polling interval in milliseconds (default: 30000)
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @param {Object} options.queryParams - Query parameters for notifications
 * @param {string} options.guard - Guard type (staff/member). If not provided, will be determined from user type
 */
export const useNotificationPolling = (options = {}) => {
  const {
    interval = 30000, // 30 seconds
    enabled = true,
    queryParams = { page: 1, status: "all", limit: 10 },
    guard: providedGuard,
  } = options;

  const userType = useUserType();
  
  // Determine guard from user type if not provided
  // user_type can be "staff" or "member" or contain "staff"/"member" in the string
  const guard =
    providedGuard ||
    (userType?.toLowerCase().includes("investor")
      ? "investor"
      : userType?.toLowerCase().includes("member")
        ? "member"
        : "staff");

  const intervalRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());
  const previousNotificationsRef = useRef([]);

  // Request browser notification permission on mount
  useEffect(() => {
    if (enabled && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [enabled]);

  // API queries (skip when not enabled)
  const notificationsQuery = useGetNotificationsQuery(
    { ...queryParams, guard },
    {
      pollingInterval: enabled ? interval : 0,
      refetchOnMountOrArgChange: true,
      skip: !enabled, // Skip query when disabled
    }
  );

  const unreadCountQuery = useGetUnreadCountQuery(guard, {
    pollingInterval: enabled ? interval : 0,
    refetchOnMountOrArgChange: true,
    skip: !enabled, // Skip query when disabled
  });

  // Detect new unread notifications and show browser notifications
  useEffect(() => {
    const currentNotifications = notificationsQuery.data?.notifications || [];
    const previousNotifications = previousNotificationsRef.current;

    if (previousNotifications.length > 0 && currentNotifications.length > 0) {
      // Find new unread notifications
      const newUnreadNotifications = currentNotifications.filter(
        (current) =>
          !current.is_read &&
          !previousNotifications.some(
            (prev) => prev.id === current.id && !prev.is_read
          )
      );

      // Show browser notification for each new unread notification
      if (newUnreadNotifications.length > 0 && Notification.permission === "granted") {
        newUnreadNotifications.forEach((notification) => {
          const basePath =
            guard === "member"
              ? "/member/notifications"
              : guard === "investor"
                ? "/invest/notifications"
                : "/super_admin/notifications";
          const notificationUrl = `${window.location.origin}${basePath}/${notification.id}`;

          const browserNotification = new Notification(notification.title || "New Notification", {
            body: typeof notification.message === "string" 
              ? notification.message 
              : (notification.message?.message || "You have a new notification"),
            icon: "/logo192.png",
            badge: "/logo192.png",
            tag: `notification-${notification.id}`,
            data: {
              url: notificationUrl,
              notificationId: notification.id,
            },
            requireInteraction: false,
          });

          // Handle notification click - navigate to notification detail page
          browserNotification.onclick = (event) => {
            event.preventDefault();
            window.focus();
            window.location.href = notificationUrl;
            browserNotification.close();
          };

          // Auto-close after 5 seconds
          setTimeout(() => {
            browserNotification.close();
          }, 5000);
        });
      }
    }

    // Update previous notifications reference
    previousNotificationsRef.current = currentNotifications;
  }, [notificationsQuery.data, guard]);

  // Manual refetch function
  const refetch = () => {
    notificationsQuery.refetch();
    unreadCountQuery.refetch();
    lastUpdateRef.current = Date.now();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Handle visibility change (pause when tab is not visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause polling when tab is not visible
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      } else {
        // Resume polling when tab becomes visible
        if (enabled) {
          refetch();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled]);

  return {
    notifications: notificationsQuery.data?.notifications || [],
    meta: notificationsQuery.data?.meta || {},
    unreadCount: typeof unreadCountQuery.data === 'number' 
      ? unreadCountQuery.data 
      : unreadCountQuery.data?.unread_count || 0,
    isLoading: notificationsQuery.isLoading || unreadCountQuery.isLoading,
    error: notificationsQuery.error || unreadCountQuery.error,
    refetch,
    lastUpdate: lastUpdateRef.current,
  };
};

/** Strip one or more stacked "(N) " prefixes from the tab title. */
export const stripNotificationBadgeFromTitle = (title = "") =>
  String(title).replace(/^(\(\d+\)\s+)+/, "");

/**
 * Hook for notification badge updates
 * Updates the browser tab title with unread count.
 *
 * Must strip existing badges before applying — multiple NotificationDropdown
 * instances (desktop + mobile) and effect re-runs otherwise stack "(1) (1) …".
 */
export const useNotificationBadge = (unreadCount, { enabled = true } = {}) => {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    const baseTitle = stripNotificationBadgeFromTitle(document.title);

    document.title =
      unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;

    return () => {
      document.title = stripNotificationBadgeFromTitle(document.title);
    };
  }, [unreadCount, enabled]);
};

/**
 * Hook for browser notifications (if user grants permission)
 */
export const useBrowserNotifications = () => {
  const requestPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return false;
  };

  const showNotification = (title, options = {}) => {
    if ("Notification" in window && Notification.permission === "granted") {
      const notification = new Notification(title, {
        icon: "/logo192.png",
        badge: "/logo192.png",
        ...options,
      });

      // Handle notification click - navigate to notification detail page
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (options.data?.url) {
          window.location.href = options.data.url;
        }
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    }
    return null;
  };

  return {
    requestPermission,
    showNotification,
    isSupported: "Notification" in window,
    permission: "Notification" in window ? Notification.permission : "denied",
  };
};
